"use client";

import Image from "next/image";
import compareCopy from "@/data/copy/compare-slider.json";
import { createCopyGetter } from "@/lib/localize-copy";
const getCompareCopy = createCopyGetter(compareCopy);
import { useEffect, useRef, useState, type CSSProperties } from "react";

import { useSiteLocale } from "@/components/layout/CcLocaleContext";
import { formatPercentLabel } from "@/components/services/seo/seo-revenue-projection";
import { useCompareSlider } from "./useCompareSlider";

type CompareImage = { src: string; alt: string };

/** Optional zoom/crop tuning when a screenshot export leaves edge bands. */
export type CompareImageFit = {
  scale?: number;
  objectPosition?: string;
  transformOrigin?: string;
};

function compareImageFitStyle(
  fit: CompareImageFit | undefined,
  side: "before" | "after",
): CSSProperties | undefined {
  if (!fit) return undefined;
  const style: Record<string, string | number> = {};
  if (fit.scale != null) style[`--cc-compare-${side}-scale`] = fit.scale;
  if (fit.objectPosition) style[`--cc-compare-${side}-position`] = fit.objectPosition;
  if (fit.transformOrigin) style[`--cc-compare-${side}-origin`] = fit.transformOrigin;
  return Object.keys(style).length ? (style as CSSProperties) : undefined;
}

type CcBeforeAfterProps = {
  before: CompareImage;
  after: CompareImage;
  /** Intrinsic size shared by both screenshots (same aspect ratio). */
  width: number;
  height: number;
  beforeLabel: string;
  afterLabel: string;
  /** Accessible name for the slider handle. */
  sliderLabel: string;
  sizes?: string;
  /** next/image quality (allowed: 75, 85 in next.config). */
  quality?: number;
  /** Optional muted loop of the new site, played over the "after" image. */
  afterVideo?: string;
  /** Second of the video that matches the "after" image; playback starts there. */
  afterVideoStart?: number;
  /** Fill a pre-sized parent (e.g. fixed-aspect stage) instead of sizing from width/height. */
  fillFrame?: boolean;
  /** Per-side cover tuning (e.g. bottom-anchored zoom on a "before" export). */
  beforeFit?: CompareImageFit;
  afterFit?: CompareImageFit;
  /** Where the divider starts, in percent of "before" shown (default 50). */
  initialPosition?: number;
};

/**
 * Two screenshots stacked in one frame; a vertical divider reveals the
 * "before" image on the left (clip-path on the top layer) and the "after"
 * image on the right. The images never move or resize — only the clip does.
 * Drag or click with mouse/touch, or use the arrow keys on the handle.
 * An optional muted video of the new site plays over the "after" image.
 * `touch-action: pan-y` keeps vertical page scrolling on touch screens.
 */
export function CcBeforeAfter({
  before,
  after,
  width,
  height,
  beforeLabel,
  afterLabel,
  sliderLabel,
  sizes = "(max-width: 809px) 100vw, 60vw",
  quality = 75,
  afterVideo,
  afterVideoStart = 0,
  fillFrame = false,
  beforeFit,
  afterFit,
  initialPosition,
}: CcBeforeAfterProps) {
  const locale = useSiteLocale();
  const copy = getCompareCopy(locale);
  const [hintVisible, setHintVisible] = useState(false);
  const hintDismissed = useRef(false);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const {
    position,
    rounded,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onLostPointerCapture,
    onKeyDown,
  } = useCompareSlider({ orientation: "horizontal", frameRef, handleRef, initialPosition });

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || typeof IntersectionObserver === "undefined") return;
    let timer: ReturnType<typeof setTimeout>;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting || hintDismissed.current) return;
      setHintVisible(true);
      timer = setTimeout(() => { setHintVisible(false); hintDismissed.current = true; }, 4500);
      observer.disconnect();
    }, {threshold: 0.35});
    observer.observe(frame);
    return () => { observer.disconnect(); clearTimeout(timer); };
  }, []);

  function dismissHint() {
    hintDismissed.current = true;
    setHintVisible(false);
  }

  // The video only loads and plays while the frame is on screen, never with
  // reduced motion; the still "after" image stays underneath as the poster.
  useEffect(() => {
    const video = videoRef.current;
    const frame = frameRef.current;
    if (!video || !frame) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let started = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          if (!started) {
            started = true;
            if (afterVideoStart > 0) video.currentTime = afterVideoStart;
          }
          video.play().catch(() => {
            // Autoplay refused: the still image stays visible.
          });
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(frame);
    return () => observer.disconnect();
  }, [afterVideo, afterVideoStart]);

  return (
    <div
      ref={frameRef}
      className={fillFrame ? "cc-compare cc-compare--fill" : "cc-compare"}
      style={
        {
          ...(fillFrame ? {} : { aspectRatio: `${width} / ${height}` }),
          "--cc-compare-pos": `${position}%`,
          ...compareImageFitStyle(afterFit, "after"),
        } as CSSProperties
      }
      data-drag-hint={hintVisible ? "true" : undefined}
      onPointerDown={(event) => { dismissHint(); onPointerDown(event); }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onLostPointerCapture={onLostPointerCapture}
    >
      {/* After (new site) is the base layer; before sits on top, clipped to the left part. */}
      <Image
        src={after.src}
        alt={after.alt}
        fill
        sizes={sizes}
        quality={quality}
        className="cc-compare__image cc-compare__image--after"
        draggable={false}
      />
      {afterVideo ? (
        <video
          ref={videoRef}
          className="cc-compare__video"
          src={afterVideo}
          muted
          loop
          playsInline
          preload="none"
          aria-hidden="true"
          onPlaying={(event) => {
            event.currentTarget.dataset.playing = "true";
          }}
        />
      ) : null}
      <div
        className="cc-compare__before"
        style={compareImageFitStyle(beforeFit, "before")}
      >
        <Image
          src={before.src}
          alt={before.alt}
          fill
          sizes={sizes}
          quality={quality}
          className="cc-compare__image cc-compare__image--before"
          draggable={false}
        />
      </div>

      <span className="cc-compare__tag cc-compare__tag--before" aria-hidden="true">
        {beforeLabel}
      </span>
      <span className="cc-compare__tag cc-compare__tag--after" aria-hidden="true">
        {afterLabel}
      </span>

      <div
        ref={handleRef}
        className="cc-compare__handle"
        role="slider"
        tabIndex={0}
        aria-label={sliderLabel}
        aria-orientation="horizontal"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={rounded}
        aria-valuetext={`${beforeLabel} ${formatPercentLabel(rounded, locale)}, ${afterLabel} ${formatPercentLabel(100 - rounded, locale)}`}
        onKeyDown={(event) => { dismissHint(); onKeyDown(event); }}
      >
        <span className="cc-compare__drag-hint" aria-hidden="true">{copy.dragHint}</span>
        <span className="cc-compare__knob" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="m9.5 8-4 4 4 4M14.5 8l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>
    </div>
  );
}
